'use client';

// Import shared components from the [identifier] route
import LegislationMetadata from '@/app/(consent-manger)/[locale]/(popover-footer)/(custome-header-design)/document/[identifier]/components/LegislationMetadata';
import { MetadataSidebar } from '@/app/(consent-manger)/[locale]/(popover-footer)/(custome-header-design)/document/[identifier]/components/MetadataSidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { documentSlug } from '@/lib/utils/documentMetadata';
import legislationServices from '@/services/legislation-services';
import type { LegislationInterface } from '@/types/api-types';
import { useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { LegislationContent } from './components/LegislationContent';
import { useGetLegislationInformation } from './hooks/useGetLegislationInformation';

interface LegislationDetailsProps {
  segments: string[];
  /**
   * Statute body fetched on the server so it is present in the HTML for
   * crawlers. `null` means the server fetch was skipped (partial IRI) or
   * failed, in which case the existing client-side fetch still runs.
   */
  initialHtml?: string | null;
  /** Sidebar metadata fetched server-side; `null` falls back to the client. */
  initialLegislation?: LegislationInterface | null;
}

export default function LegislationDetails({
  segments,
  initialHtml = null,
  initialLegislation = null,
}: LegislationDetailsProps) {
  const t = useTranslations('LegislationDetails');
  const contentRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const locale = (params?.['locale'] as string) || 'sk';
  // Issue #327 gap 1: a cross-reference link forwards the citing law's
  // effective date as ?asOf=YYYYMMDD (see link-options-menu.tsx) so this
  // resolution picks THAT version instead of always "latest".
  const asOf = useSearchParams().get('asOf');

  // Legislation data state (moved from LegislationHeader.tsx).
  // Seeded from the server render when available, so the sidebar paints with
  // the first frame instead of flashing a skeleton and refetching.
  const [legislation, setLegislation] = useState<LegislationInterface | null>(
    initialLegislation
  );
  const [legislationError, setLegislationError] = useState<string | null>(null);
  const [isLegislationLoading, setIsLegislationLoading] = useState<boolean>(
    initialLegislation === null
  );
  const [iri, setIri] = useState<string>('');
  const [isResolving, setIsResolving] = useState<boolean>(false);

  // Check if segments need resolution (more than 5 segments = paragraph reference)
  useEffect(() => {
    const resolveIriIfNeeded = async () => {
      // If more than 5 segments, it's a paragraph reference that needs resolution
      if (segments.length > 5) {
        const iriPath = segments.join('/');
        setIsResolving(true);

        try {
          const response = await legislationServices.getLegislationRedirectIri(
            iriPath,
            asOf
          );
          if (!response?.data) {
            // No version in force on `asOf` (or an unknown IRI). The hook
            // below runs the same resolution and surfaces the message;
            // this resolver only has to stop holding the skeleton up.
            setIsResolving(false);
            return;
          }
          const resolvedIri = response.data;
          const [resolvedPath, hash] = resolvedIri.split('#');

          // Remove leading slash for URL construction
          const cleanPath = resolvedPath?.startsWith('/')
            ? resolvedPath.slice(1)
            : resolvedPath;

          // Redirect with hash preserved
          const newUrl = `/${locale}/${documentSlug(locale)}/${cleanPath}${hash ? `#${hash}` : ''}`;
          window.location.href = newUrl;
        } catch (err) {
          console.error('[LegislationDetails] Failed to resolve IRI:', err);
          setIsResolving(false);
        }
      }
    };

    resolveIriIfNeeded();
  }, [segments, locale, asOf]);

  const { isLoading, error } = useGetLegislationInformation({ segments });

  // Fetch legislation data (logic moved from LegislationHeader.tsx).
  // Skipped when the server already supplied it — refetching identical data on
  // mount would waste a request and re-introduce the skeleton flash.
  useEffect(() => {
    if (initialLegislation) {
      setIri(segments.join('/'));
      return;
    }

    if (segments.length === 5) {
      setIsLegislationLoading(true);
      const iriPath = segments.join('/');
      setIri(iriPath);
      const fetchLegislation = async () => {
        const legislationResponse =
          await legislationServices.getLegislationInfo(iriPath);
        if (legislationResponse.data) {
          setLegislation(legislationResponse.data);
        } else {
          setLegislationError(legislationResponse.message);
        }
        setIsLegislationLoading(false);
      };
      fetchLegislation();
    }
  }, [segments, initialLegislation]);

  if (legislationError) {
    console.error('LegislationDetails error:', legislationError);
  }

  if (error) {
    return (
      <div className='my-8'>
        <Card>
          <CardContent className='pt-6'>
            <div className='text-center py-8'>
              <h2 className='text-xl font-semibold text-status-error mb-2'>
                {t('error') || 'Error'}
              </h2>
              <p className='text-content-secondary'>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showLoading = isLoading || isLegislationLoading || isResolving;

  // Loading skeleton for sidebar
  const SidebarSkeleton = () => (
    <div className='p-4 space-y-4'>
      <Skeleton className='h-9 w-full animate-pulse' />
      <Skeleton className='h-5 w-2/3 animate-pulse' />
      <div className='space-y-2'>
        <Skeleton className='h-10 w-full animate-pulse' />
        <Skeleton className='h-10 w-full animate-pulse' />
        <Skeleton className='h-10 w-full animate-pulse' />
      </div>
      <Skeleton className='h-8 w-1/2 animate-pulse' />
      <div className='flex flex-wrap gap-2'>
        <Skeleton className='h-6 w-20 animate-pulse' />
        <Skeleton className='h-6 w-24 animate-pulse' />
        <Skeleton className='h-6 w-16 animate-pulse' />
      </div>
    </div>
  );

  return (
    <div
      className='grid grid-cols-[1fr_auto] w-full h-[calc(100vh-64px)]'
      ref={contentRef}
    >
      {/* Main content area */}
      <div className='h-full overflow-y-auto min-w-0'>
        {segments.length === 5 && (
          <LegislationContent segments={segments} initialHtml={initialHtml} />
        )}
      </div>

      {/* Metadata sidebar */}
      <MetadataSidebar title={t('legislationMetadata') || 'Metadáta zákona'}>
        {showLoading ? (
          <SidebarSkeleton />
        ) : legislation ? (
          <LegislationMetadata
            legislationInfo={legislation}
            iri={iri}
            documentId={legislation.document_content_id}
          />
        ) : null}
      </MetadataSidebar>
    </div>
  );
}
